import { randomUUID } from '../../../shared/request-id.ts';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { getToken, getUser } from './auth.js';
import { readBoundRecorderStatus } from './travelRecorderOwner.ts';
import { acknowledgeJourneyItems, appendJourneyCommand, replaceJourneyCommand, readJournal, updateJournal, type LocalJourney, type JournalCommand, type JournalEvidence, type RejectedJourneyEvidence } from './travelJournal.ts';
import type { JourneyPending } from './travelControllerEngine.ts';

type Identity = Pick<LocalJourney, 'owner' | 'sessionId' | 'costId' | 'deviceId'>;
export interface JourneyEvidenceInput {
  stopId?: string; image: string; odometer: number; odometerUnit: string;
  role?: 'ODOMETER_ARRIVAL' | 'ODOMETER_DEPARTURE' | 'WORK_PHOTO'; capturedAt?: string;
}
interface NativeRecorder {
  capabilities(): Promise<{ version: number; platform: string; background: boolean }>;
  requestPermission(v: { owner: string }): Promise<{ granted: boolean }>;
  prepare(v: LocalJourney): Promise<LocalJourney>;
  start(v: Identity): Promise<LocalJourney>;
  markArrival(v: Identity & { fields: Record<string, unknown> }): Promise<LocalJourney>;
  markDeparture(v: Identity & { fields: Record<string, unknown> }): Promise<LocalJourney>;
  correctStop(v: Identity & { fields: Record<string, unknown> }): Promise<LocalJourney>;
  replaceCommand(v: Identity & { operationId: string; fields: Record<string, unknown>; durable: boolean }): Promise<LocalJourney>;
  status(v: { owner: string }): Promise<Partial<LocalJourney> & { ownerLockId?: string }>;
  bindOwner(v: { owner: string; lockId: string }): Promise<Partial<LocalJourney> & { ownerLockId?: string }>;
  leaveOwner(v: { owner: string }): Promise<unknown>;
  stop(v: { owner: string; terminal: boolean } & Partial<Identity>): Promise<LocalJourney>;
  readPending(v: Identity): Promise<JourneyPending>;
  acknowledgeUploaded(v: Identity & { operationIds?: string[]; throughSequence?: number; evidenceIds?: string[] }): Promise<LocalJourney>;
  saveEvidence(v: Identity & JourneyEvidenceInput & { replaceEvidenceIds?: string[] }): Promise<LocalJourney>;
  complete(v: Identity): Promise<void>;
}
const native = registerPlugin<NativeRecorder>('TravelRecorder');
export const nativeRecorderAvailable = () => Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('TravelRecorder');
export function journeyOwner() { const user = getUser(); return user?.id && user.organization?.id ? `${user.organization.id}:${user.id}` : null; }
function owner() { const value = journeyOwner(); if (!value) throw new Error('AUTH_REQUIRED'); return value; }
function assertOwner(value: string) { if (journeyOwner() !== value) throw new Error('TRAVEL_SESSION_CONFLICT'); }
const identity = (session: Identity): Identity => ({ owner: session.owner, sessionId: session.sessionId, costId: session.costId, deviceId: session.deviceId });
function assertSession(current: LocalJourney | null, expected: Identity): asserts current is LocalJourney {
  assertOwner(expected.owner);
  if (!current || Object.entries(identity(expected)).some(([key, value]) => current[key as keyof Identity] !== value)) throw new Error('TRAVEL_SESSION_CONFLICT');
}
export const recorderCapabilities = () => nativeRecorderAvailable() ? native.capabilities() : Promise.resolve({ version: 1, platform: 'web', background: false });
export async function requestJourneyLocation() {
  const actor = owner(); if (!nativeRecorderAvailable()) return false;
  const result = await native.requestPermission({ owner: actor }); assertOwner(actor); return result.granted;
}
export async function readLocalJourney(): Promise<LocalJourney | null> {
  const actor = journeyOwner(); if (!actor) return null;
  const result = nativeRecorderAvailable() ? await readBoundRecorderStatus(actor, {
    currentOwner: () => getToken() ? journeyOwner() : null,
    status: () => native.status({ owner: actor }), bind: lockId => native.bindOwner({ owner: actor, lockId }),
    leave: () => native.leaveOwner({ owner: actor }),
  }) : await readJournal<LocalJourney>(actor);
  assertOwner(actor);
  if (!result?.costId) return null;
  if (result.owner !== actor) throw new Error('TRAVEL_SESSION_CONFLICT');
  return result as LocalJourney;
}
export async function prepareRecorder(session: LocalJourney) {
  assertOwner(session.owner);
  if (nativeRecorderAvailable()) { const result = await native.prepare(session); assertSession(result, session); return result; }
  return updateJournal<LocalJourney>(session.owner, current => {
    assertOwner(session.owner);
    if (current) assertSession(current, session);
    return current || session;
  });
}
export async function recorderCommand(action: JournalCommand['action'], fields: Record<string, unknown> = {}, expected?: LocalJourney) {
  const actor = owner(); if (expected) assertOwner(expected.owner);
  // Emergency/terminal stop reaches native hardware before any storage read.
  if (nativeRecorderAvailable() && (action === 'end' || action === 'health')) {
    const result = await native.stop({ owner: actor, ...(expected ? identity(expected) : {}), terminal: action === 'end' });
    assertOwner(actor); if (expected) assertSession(result, expected); return result;
  }
  const session = expected || await readLocalJourney();
  if (!session) throw new Error('NO_JOURNEY'); assertOwner(actor); assertOwner(session.owner);
  if (nativeRecorderAvailable()) {
    const target = identity(session);
    const result = action === 'activate' ? await native.start(target)
      : action === 'arrive' ? await native.markArrival({ ...target, fields })
      : action === 'depart' ? await native.markDeparture({ ...target, fields })
      : await native.correctStop({ ...target, fields });
    assertSession(result, session); return result;
  }
  return updateJournal<LocalJourney>(actor, current => { assertSession(current, session); return appendJourneyCommand(current, action, fields); });
}
export async function pendingRecorder(expected?: LocalJourney): Promise<JourneyPending> {
  const session = expected || await readLocalJourney();
  if (!session) return { commands: [], points: [], evidence: [] };
  assertOwner(session.owner);
  if (nativeRecorderAvailable()) { const result = await native.readPending(identity(session)); assertOwner(session.owner); return result; }
  const current = await readLocalJourney(); assertSession(current, session);
  // Stop photos must drain even if a final-photo draft was captured first.
  const evidence = current.evidence.find(item => !!item.stopId) || current.evidence.find(item => !item.stopId);
  return { commands: current.commands, points: [], evidence: evidence ? [evidence] : [] };
}
export async function replaceRecorderCommand(session: LocalJourney, operationId: string, fields: Record<string, unknown>, durable: boolean) {
  assertOwner(session.owner);
  // Validate the same bounded field contract before either adapter is called.
  replaceJourneyCommand(session, operationId, fields, durable);
  if (nativeRecorderAvailable()) {
    const result = await native.replaceCommand({ ...identity(session), operationId, fields, durable }); assertSession(result, session); return result;
  }
  return updateJournal<LocalJourney>(session.owner, current => { assertSession(current, session); return replaceJourneyCommand(current, operationId, fields, durable); });
}
export async function acknowledgeRecorder(value: { operationIds?: string[]; throughSequence?: number; evidenceIds?: string[] }, session: LocalJourney) {
  assertOwner(session.owner); const acknowledgement = { ...identity(session), ...value };
  if (nativeRecorderAvailable()) { const result = await native.acknowledgeUploaded(acknowledgement); assertSession(result, session); return result; }
  return updateJournal<LocalJourney>(session.owner, current => { assertSession(current, session); return acknowledgeJourneyItems(current, acknowledgement); });
}
export async function saveJourneyEvidence(value: JourneyEvidenceInput) {
  const session = await readLocalJourney(); if (!session) throw new Error('NO_JOURNEY');
  const fields = { ...value, role: value.role || 'ODOMETER_ARRIVAL', capturedAt: value.capturedAt || new Date().toISOString() };
  if (!Number.isFinite(value.odometer) || value.odometer < 0 || !['KM', 'MI'].includes(value.odometerUnit)
    || value.odometerUnit !== session.odometerUnit || !value.image || !Number.isFinite(Date.parse(fields.capturedAt))) throw new Error('INVALID_EVIDENCE');
  if (!value.stopId && session.state !== 'ENDING') throw new Error('TRAVEL_END_REQUIRED');
  if (!value.stopId && await readJournal(`${session.owner}:finish`)) throw new Error('SYNC_PENDING');
  const rejectionKey = `${session.owner}:rejected-evidence:${session.sessionId}`;
  const rejected = await readJournal<RejectedJourneyEvidence[]>(rejectionKey);
  const replaceEvidenceIds = new Set((rejected || []).filter(item => item.stopId === value.stopId && item.role === fields.role).map(item => item.id));
  assertOwner(session.owner);
  if (nativeRecorderAvailable()) {
    const result = await native.saveEvidence({ ...identity(session), ...fields, replaceEvidenceIds: [...replaceEvidenceIds] }); assertSession(result, session); return result;
  }
  const id = randomUUID();
  return updateJournal<LocalJourney>(session.owner, current => {
    assertSession(current, session);
    if (current.state === 'COMPLETED' || current.state === 'PREPARING') throw new Error('TRAVEL_INVALID_TRANSITION');
    const evidence: JournalEvidence = { id, operationId: id, ...fields };
    return { ...current, evidence: [...current.evidence.filter(item => (value.stopId || item.stopId) && !replaceEvidenceIds.has(item.id)), evidence] };
  });
}
export async function clearCompletedRecorder(expected?: LocalJourney) {
  const session = expected || await readLocalJourney(); if (!session) return;
  assertOwner(session.owner);
  if (nativeRecorderAvailable()) { await native.complete(identity(session)); assertOwner(session.owner); return; }
  await updateJournal<LocalJourney>(session.owner, current => {
    assertSession(current, session);
    if (!['ENDING', 'COMPLETED'].includes(current.state) || current.recording) throw new Error('TRAVEL_END_REQUIRED');
    if (current.commands.length || current.evidence.length) throw new Error('SYNC_PENDING');
    return null;
  });
}
export function installRecorderSessionGuard() {
  window.addEventListener('auth:leaving-session', (event) => {
    const previous = (event as CustomEvent<{ owner: string }>).detail?.owner;
    // Retain the previous journal, stop hardware, and lock bundled recovery
    // mutations until that owner re-establishes the current session.
    if (nativeRecorderAvailable() && previous) void native.leaveOwner({ owner: previous }).catch(() => undefined);
  });
}
